import React from "react";
import Bugsnag from "@bugsnag/js";
import { Alert, Button } from "antd";
import type { IRole } from "@aspen/libs";
import { bugsnagInit, formatBugsnagMessage, HOME_PATH, isDev } from "@aspen/libs";
import type { BugsnagErrorBoundary } from "@bugsnag/plugin-react";
import { withRouter } from "next/router";
import { Header } from "@aspen/widgets";
import type { IInjectProps } from "@aspen/model";

interface ErrorBoundaryProps extends IInjectProps {
  children: React.ReactNode;
  routeConfig?: IRole;
  bugsnagDisable?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  onLine: boolean;
  errorInfo: string | null;
}

class ErrorBoundaryWrapper extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private uploadBugsnag: boolean;
  private ErrorBoundary?: BugsnagErrorBoundary;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      onLine: true,
      errorInfo: null,
    };

    this.uploadBugsnag = !isDev && !props.bugsnagDisable;
    if (this.uploadBugsnag) {
      bugsnagInit();
      this.ErrorBoundary = Bugsnag.getPlugin("react")?.createErrorBoundary(React);
    }
  }

  componentDidMount() {
    window.addEventListener("offline", this.handleOffline);
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("error", this.handleWindowError);
  }

  componentWillUnmount() {
    window.removeEventListener("offline", this.handleOffline);
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("error", this.handleWindowError);
  }

  handleOffline = () => this.setState({ onLine: false });
  handleOnline = () => this.setState({ onLine: true });

  handleWindowError = (e: ErrorEvent) => {
    // 忽略ResizeObserver错误
    if (e.message === "ResizeObserver loop limit exceeded") {
      e.preventDefault();
      return;
    }
    // 报告错误给 Bugsnag
    if (this.uploadBugsnag) {
      Bugsnag.notify(formatBugsnagMessage(e.message, "window-error"));
    }

    if (isDev) {
      console.error("Global error:", e);
    }
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      errorInfo: isDev ? `${error.name}: ${error.message}\n${error.stack}` : null,
    };
  }

  componentDidCatch(error: Error) {
    // 报告错误给 Bugsnag
    if (this.uploadBugsnag) {
      Bugsnag.notify(formatBugsnagMessage(String(error), "boundary-error"));
    }

    if (isDev) {
      console.error("Component error:", error);
    }
  }

  handleResetError = () => {
    // 开发环境：重置状态
    // 生产环境：刷新页面
    if (isDev) {
      this.setState({
        hasError: false,
        errorInfo: null,
      });
    } else {
      window.location.reload();
    }
  };

  // 通用错误视图框架
  renderErrorView(title: string, description: React.ReactNode, actions?: React.ReactNode) {
    return (
      <div className="error-view">
        {this.props.routeConfig ? (
          <Header router={this.props.router} routeConfig={this.props.routeConfig} />
        ) : null}
        <div className="p-40 text-center">
          <h2 className="pb-8">{title}</h2>
          {description}
          {actions ? <div className="mt-6 flex !justify-center gap-4">{actions}</div> : null}
        </div>
      </div>
    );
  }

  // 生产环境错误视图
  ProductionErrorView = () => {
    return this.renderErrorView(
      "Something went wrong",
      <p>Something went wrong.</p>,
      <>
        <Button
          type="primary"
          onClick={() => {
            this.props.router.replace(HOME_PATH);
          }}>
          Back to Home
        </Button>
        <Button type="primary" onClick={this.handleResetError}>
          Try Again
        </Button>
      </>,
    );
  };

  // 开发环境错误视图
  DevelopmentErrorView = () => {
    return (
      <div className="error-view">
        <Alert
          type="error"
          message="Component Error"
          description={
            <pre className="whitespace-pre-wrap overflow-x-auto">{this.state.errorInfo}</pre>
          }
          showIcon
          closable
        />
        {this.renderErrorView("Something went wrong.", <p>Please fix the error in your code</p>)}
      </div>
    );
  };

  renderErrorContent() {
    if (isDev) {
      return this.DevelopmentErrorView();
    }
    return this.ProductionErrorView();
  }

  render() {
    // 网络离线状态
    if (!this.state.onLine) {
      return (
        <div className="text-center p-10">
          <Alert
            message="No network connection"
            description="Please check your network settings and try again"
            type="warning"
            showIcon
          />
          <Button type="primary" onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      );
    }

    if (this.state.hasError) {
      return this.renderErrorContent();
    }

    if (this.uploadBugsnag && this.ErrorBoundary) {
      return (
        <this.ErrorBoundary FallbackComponent={this.ProductionErrorView}>
          {this.props.children}
        </this.ErrorBoundary>
      );
    }

    return this.props.children;
  }
}

const WithErrorBoundary = withRouter((props: ErrorBoundaryProps) => (
  <ErrorBoundaryWrapper key={props.router.pathname} {...props}>
    {props.children}
  </ErrorBoundaryWrapper>
));

export { WithErrorBoundary };
